/* eslint-disable camelcase */

import { pickBy } from 'lodash';
import { HELMET_LOADING_TITLE } from '../constants';
import { trackPageView } from '../iterableUtils';
import { ANALYTICS_IS_DISABLED } from './variables';
import * as creators from './creators';
import {
    DataLayerEvent,
    Impression,
    Product,
    ProductDetail,
    ProductInList,
    ProductCartItem,
    Purchase,
} from './types';

import wishListEvents, { WishListListName } from './events/wishList';
import quickPayEvents, { QuickPayLabel } from './events/quickPay';
import accountEvents, { AccountData } from './events/account';
import impressionEvents from './events/impression';
import productEvents from './events/product';
import checkoutEvents from './events/checkout';
import navigationEvents from './events/navigation';

function pickDefined<T extends object>(obj: T) {
    return pickBy<T>(obj, (v) => v !== undefined);
}

export class Analytics {
    debug: boolean = false;
    testMode: boolean = false;
    disDlvWarn: boolean = false;
    disFbqWarn: boolean = false;

    // Events
    wishList: {
        add(listName: WishListListName, product: Product): Analytics;
        remove(listName: WishListListName, product: Product): Analytics;
    };

    quickPay: {
        readonly label: QuickPayLabel;
        displayButton(): Analytics;
        transactionStart(): Analytics;
        transactionSuccessed(): Analytics;
        transactionFailed(): Analytics;
    };

    account: {
        userId(userId: string | number): Analytics;
        contactInfo(data: AccountData): Analytics;
        subscribe(data: AccountData): Analytics;
    };

    impression: { dispatch(impression: Impression | Impression[]): Analytics };

    product: {
        click(
            payload: Product | ProductInList | Product[] | ProductInList[],
            listName?: string
        ): Analytics;
        clickCheckAvailability(payload: Product | Product[]): Analytics;
        selectAvailabilityDates(
            payload: Product | Product[],
            { startDate, endDate }: { startDate: string; endDate?: string }
        ): Analytics;
        selectAvailabilityDay(
            payload: Product | Product[],
            { dayDate }: { dayDate: string }
        ): Analytics;
        detail(payload: ProductDetail | ProductDetail[], listName: string): Analytics;
        addToCart(payload: ProductCartItem | ProductCartItem[]): Analytics;
        viewCart(payload: ProductCartItem | ProductCartItem[]): Analytics;
    };

    checkout: {
        step({
            products,
            step,
            option,
        }: {
            products: Product[];
            step: number;
            option: string;
        }): Analytics;

        transactionComplete({
            products,
            purchase,
        }: {
            products: ProductCartItem[];
            purchase: Purchase;
        }): Analytics;
    };

    navigation: {
        clickedLogotype(): Analytics;
        clickedCartButton(): Analytics;
        clickedWishButton(): Analytics;
        clickedSearchButton({
            destination,
            category,
        }: {
            destination?: { name: string };
            category?: { name: string };
        }): Analytics;
        selectedSearch({
            destination,
            category,
        }: {
            destination?: { name: string };
            category?: { name: string };
        }): Analytics;
        selectedSearchDates({
            startDate,
            endDate,
        }: {
            startDate?: Date;
            endDate?: Date;
        }): Analytics;
    };

    constructor() {
        this.wishList = wishListEvents(this);
        this.quickPay = quickPayEvents(this);
        this.account = accountEvents(this);
        this.impression = impressionEvents(this);
        this.product = productEvents(this);
        this.checkout = checkoutEvents(this);
        this.navigation = navigationEvents(this);
    }

    enableTestMode(b: boolean) {
        this.testMode = b;
        return this;
    }

    enableDebug(b: boolean) {
        this.debug = b;
        return this;
    }

    get dataLayer(): Function {
        if (this.testMode) {
            return console.log;
        }

        const dlv = ANALYTICS_IS_DISABLED ? 'dataLayerTM' : 'dataLayer';

        try {
            if (window && window[dlv]) {
                return this.debug
                    ? (...args: any) => {
                          console.log(dlv, ...args);
                          return window[dlv].push(...args);
                      }
                    : window[dlv].push;
            }
            throw new Error(`DataLayer (${dlv}) is undefined.`);
        } catch (err) {
            if (!this.disDlvWarn) {
                console.warn(err);
            }
            this.disDlvWarn = true;
            return console.log;
        }
    }

    get fbq(): Function {
        if (this.testMode) {
            return console.log;
        }

        const fbq = ANALYTICS_IS_DISABLED ? 'fbqTM' : 'fbq';

        try {
            if (window && window[fbq]) {
                return this.debug
                    ? (...args: any) => {
                          console.log(fbq, ...args);
                          return window[fbq](...args);
                      }
                    : window[fbq];
            }
            throw new Error(`FBQ (${fbq}) is undefined.`);
        } catch (err) {
            if (!this.disFbqWarn) {
                console.warn(err);
            }

            this.disFbqWarn = true;
            return console.log;
        }
    }

    get creators() {
        return creators;
    }

    wrapAvoidError(handler: Function) {
        if (typeof handler !== 'function') {
            return false;
        }

        try {
            return handler();
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    pageview(page = '/', title = '') {
        this.dataLayer({
            event: 'pageview',
            page: { path: page, title },
        });

        trackPageView({
            path: page,
            title,
        });

        return true;
    }

    onClickStickyCheckAvailabilityButton() {
        this.DataLayerEvent({
            category: 'Check Availability Widget',
            action: 'clicked',
            label: 'button',
            nonInteraction: false,
        });
    }
    openNavigation() {
        this.DataLayerEvent({
            category: 'Menu',
            action: 'open',
            label: 'button',
            nonInteraction: false,
        });
    }
    closeNavigation() {
        this.DataLayerEvent({
            category: 'Menu',
            action: 'close',
            label: 'button',
            nonInteraction: false,
        });
    }

    OneTapEvent(select_by: string) {
        if (!select_by) {
            return;
        }
        const action = select_by === 'auto' ? 'auto' : 'clicked';
        const label =
            select_by === 'auto' || select_by === 'user' ? 'returning-one-tap' : 'one-tap';
        this.DataLayerEvent({
            category: 'Authorization',
            action,
            label,
            nonInteraction: false,
        });
    }

    wpageview() {
        const v = window.document.title;
        const z = this.wpage;
        this.dataLayer({
            event: 'pageview',
            page: { path: z, title: v },
        });

        trackPageView({
            path: z,
            title: v,
        });

        if (v === HELMET_LOADING_TITLE) {
            console.warn(
                '[Analytics]',
                'Everything is very bad! We spotted the incomplete load at the `wpageview` event',
                z
            );
        }

        return this;
    }

    get wpage() {
        return window.location.pathname + window.location.search;
    }

    /**
     * Additional Events
     *  - category
     *  - action
     *  - label
     */

    DataLayerEvent(payload: DataLayerEvent) {
        const defined = pickDefined<DataLayerEvent>(payload);
        const event: DataLayerEvent & { event: 'custom' } = {
            event: 'custom',
            ...(defined as DataLayerEvent),
        };

        this.dataLayer(event);

        return this;
    }

    event(payload: DataLayerEvent) {
        return this.DataLayerEvent(payload);
    }
}

const AnalyticsInstance = new Analytics();

export { AnalyticsInstance };
